import React, { useEffect, useReducer, createContext, Dispatch } from "react";
import { KEY_ACCESS_TOKEN, KEY_REFRESH_TOKEN } from "../constant";
import { AuthAction, AuthState } from "../../interfaces/Contexts";
import { ReactChildren } from "../../interfaces/Common";

export const initialState: AuthState = {
  isAuthenticated: false,
  /* Starts true: on a fresh page load we do not yet know whether there is a
     stored session. Guards wait for this to clear instead of redirecting. */
  isRestoring: true,
  user: null,
  search: null,
};

const reducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case "LOGIN":
      const userPayload = action.payload || { user: null };
      localStorage.setItem(KEY_REFRESH_TOKEN, userPayload.user?.refreshToken?.token ?? "");
      localStorage.setItem(KEY_ACCESS_TOKEN, userPayload.user?.accessToken?.token ?? "");
      localStorage.setItem("user", JSON.stringify(userPayload.user));
      return {
        ...state,
        isAuthenticated: userPayload.user && userPayload.user === null ? false : true,
        isRestoring: false,
        user: userPayload.user || null,
      };
    case "LOGOUT":
      localStorage.clear();
      return {
        ...state,
        isAuthenticated: false,
        isRestoring: false,
        user: null,
        search: null,
      };
    /* The stored-session check finished and found nothing. Without this the
       guards would wait forever on a genuinely logged-out visitor. */
    case "RESTORE_DONE":
      return { ...state, isRestoring: false };
    default:
      return state;
  }
};

export const AuthContext = createContext<{
  state: AuthState;
  dispatch: Dispatch<AuthAction>;
}>({ state: initialState, dispatch: () => {} });

export const AuthContextProvider: React.FunctionComponent<ReactChildren> = (props) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  /* Runs once on mount. Either branch settles isRestoring, so the route guards
     stop waiting: a stored session logs the user straight back in, and no
     stored session falls through to the login redirect as before. */
  useEffect(() => {
    let user = null;
    try {
      user = JSON.parse(localStorage.getItem("user") ?? "null");
    } catch (e) {
      user = null; /* corrupt entry: treat as logged out rather than hanging */
    }
    if (user) {
      dispatch({
        type: "LOGIN",
        payload: {
          user,
        },
      });
    } else {
      dispatch({ type: "RESTORE_DONE" });
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        state,
        dispatch,
      }}
    >
      {props.children}
    </AuthContext.Provider>
  );
};
